import { XMarkIcon } from "@heroicons/react/24/solid";
import { CheckIcon } from "lucide-react";
import { title } from "process";
import React, { useEffect, useState } from "react";

interface ICheckBoxProps {
	id?: string;
	title?: string;
	onChange: (value: boolean) => void;
	checked?: boolean; // Controlled prop for external state management
	disabled?: boolean; // Optional prop to disable the checkbox
}

const CheckBox: React.FC<ICheckBoxProps> = ({
	id,
	title,
	onChange,
	checked: controlledChecked,
	disabled = false,
}) => {
	const [internalChecked, setInternalChecked] = useState<boolean>(false);
	const isControlled = controlledChecked !== undefined;

	const handleToggle = () => {
		if (disabled) return;
		if (isControlled) {
			onChange(!controlledChecked);
		} else {
			const newValue = !internalChecked;
			setInternalChecked(newValue);
			onChange(newValue);
		}
	};

	useEffect(() => {
		if (isControlled) {
			setInternalChecked(controlledChecked!);
		}
	}, [controlledChecked, isControlled]);

	return (
		<label
			id={id}
			data-testid={id}
			className={`flex items-center justify-center gap-2 select-none ${
				disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer"
			}`}
		>
			<input
				type="checkbox"
				className="hidden"
				checked={internalChecked}
				onChange={handleToggle}
				disabled={disabled}
			/>
			<div
				className={`h-6 w-6 flex items-center justify-center ${
					disabled ? "bg-gray-600" : "bg-sky-600"
				}`}
			>
				{internalChecked ? (
					<CheckIcon
						className={`w-4 h-4 font-semibold text-white`}
						strokeWidth={2}
					/>
				) : (
					<XMarkIcon className={`w-4 h-4 font-semibold text-white`} />
				)}
			</div>
			{title && (
				<span
					className={`font-Poppins font-semibold text-md ${
						disabled ? "text-gray-400" : ""
					}`}
				>
					{title}
				</span>
			)}
		</label>
	);
};

export default CheckBox;
